<!-- pages/editor/multi.vue -->
<template>
  <div class="layout" :class="{ 'is-mobile-open': mobileOpen, 'is-tablet': isTablet, 'layout--embedded-tile': isEmbeddedTileMode }">
    <div v-if="rootCategories.length && !isEmbeddedTileMode" class="cat-col">
      <CategoryScrollPanel
        ref="rootPanelRef"
        :categories="rootCategoriesWithCounts"
        :active-category-id="activeCategoryId"
        :horizontal="isMobile"
        :show-all="true"
        @select="onRootCategorySelect"
      />
    </div>

    <aside
        v-if="!isEmbeddedTileMode"
        class="sidebar"
        :class="{ open: mobileOpen, disabled: !hasSelection, overlay: isTablet }"
        :aria-hidden="(isMobile || isTablet) && !mobileOpen"
        aria-label="Konfiguration"
    >
      <button v-if="(isMobile || isTablet) && mobileOpen" class="sidebar-close-btn" type="button" @click="closeSidebar" aria-label="Schließen" title="Schließen">
        ✕
      </button>

      <Sidebar
          ref="sidebarRef"
          :key="sidebarStateKey"
          :logo-url="logoUrl || undefined"
          :product-id="sidebarProductId ?? undefined"
          :category="sidebarCategoryId!"
          :selected-ids="selectedIds"
          :show-upload="false"
          :is-multi-mode="true"
          :total-image-count="totalImageCount"
          :subcategories="sidebarSubcats"
          :active-subcategory-id="sidebarActiveSubcatId"
          :parent-category-id="sidebarParentCatId"
          @applied="onSidebarApplied"
          @add-to-cart="onSidebarAddToCart"
          @db-updated="onDbUpdated"
          @apply-size-local="onSidebarApplySizeLocal"
          @options-preview="onSidebarOptionsPreview"
          @select-subcategory="onSubCategorySelect"
          @navigate="onSidebarNavigate"
          @price-change="(label: string) => sidebarTotalPriceLabel = label"
      />
    </aside>

    <main class="main">
      <button
        v-if="!isEmbeddedTileMode && (isTablet || isMobile) && !mobileOpen && hasSelection"
        class="tablet-sidebar-toggle"
        :class="{ 'tablet-sidebar-toggle--mobile': isMobile }"
        type="button"
        @click="openSidebar"
      >
        <Pencil :size="16" />
        <span>Konfiguration öffnen</span>
      </button>
      <ImageTool
          :key="`${categoryId ?? 0}_${productId ?? 0}`"
          ref="miRef"
          :category-id="categoryId ?? 0"
          :product-id="productId ?? 0"
          :crop-engine="cropEngine"
          :product-name="productId ? productName : undefined"
          :category-label="categoryLabel"
          :category-titles-map="categoryTitlesMap"
          :category-thumbnails-map="categoryThumbnailsMap"
          :category-root-ids-map="categoryRootIdsMap"
          :root-category-titles-map="rootCategoryTitlesMap"
          :category-order-map="categoryOrderMap"
          :root-category-order-map="rootCategoryOrderMap"
          :size-options="sizeOptions"
          :tile-variant-groups="mappedCfg?.groups || []"
          :preselect-ids="preselectIds"
          :priority-category-ids="priorityCategoryIds.length ? priorityCategoryIds : undefined"
          :logo-url="logoUrl || undefined"
          :total-price-label="editorTotalPriceLabel || sidebarTotalPriceLabel || undefined"
          :category-editor-types="categoryEditorTypes"
          :category-editor-configs="categoryEditorConfigs"
          :preview-model-config="previewModelConfig"
          sidebar-mode="manual"
          :is-multi-mode="true"
          @initial-loading="onEditorInitialLoading"
          @selection-change="selectIds"
          @open-details="openSidebar"
          @meta-changed="onMetaChanged"
          @navigate="onImageToolNavigate"
      />
    </main>

    <div v-if="!isEmbeddedTileMode && (isMobile || isTablet) && mobileOpen" class="backdrop" @click="closeSidebar" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Pencil } from 'lucide-vue-next'
import { quoteBulk, type PriceRequest } from '../../services/priceApi'

import { useRouteQueryIds } from '../../composables/useRouteQueryIds'
import { getWidgetConfig } from '../../runtime/widgetConfig'
import { resolvePreview3dOverrides } from '../../utils/editorConfigResolver'
import { resolvePrintboxFamilyId } from '../../utils/printboxCategory'
import { useToast } from '../../composables/useToast'
import { useImageDB, type ImageRecord } from '../../utils/imageDb'
import type { EditorCategoryTreeItem, EditorCategoryTreeResponse } from '../../services/editorCategoryApi'
import { fetchEditorCatalogBootstrap, type EditorCatalogBootstrapResponse } from '../../services/editorCatalogApi'
import { mergeServerAndLocalImageRecord } from '../../services/projectUploadBridge'
import { fetchProjectUploads, invalidateProjectUploadsCache, mapRemoteUploadToRecordMeta } from '../../services/projectUploadBridge'
import { useProjectStore } from '../../store/useProjectStore'
import { useEntryUploadHandoffStore } from '../../store/useEntryUploadHandoffStore'
import { useTransientUploadStore } from '../../store/useTransientUploadStore'
import Sidebar from '../editor/configurator/sidebar.vue'
import CategoryScrollPanel from '../editor/CategoryScrollPanel.vue'
import ImageTool from '../editor/image-tool.vue'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const db = useImageDB('imageDB')
const project = useProjectStore()
const entryUploadHandoff = useEntryUploadHandoffStore()
const transientUploads = useTransientUploadStore()
const isEmbeddedTileMode = computed(() => String(route.query.embedded || '').trim() === 'tile')
const EDITOR_IMAGES_CHANGED_EVENT = 'pfe:editor-images-changed'

/* -------------------- responsive (via MediaQueryList) -------------------- */
const isMobile = ref(false)
const isTablet = ref(false)
let mql: MediaQueryList | null = null
let mqlHandler: ((e: MediaQueryListEvent) => void) | null = null
let tabletMql: MediaQueryList | null = null
let tabletMqlHandler: ((e: MediaQueryListEvent) => void) | null = null

onMounted(() => {
  project.loadFromStorage()
  const ensureProjectReady = project.projectCode.value
    ? Promise.resolve()
    : project.ensureProject().then(() => undefined).catch(() => undefined)
  loadCategoryTree().catch(() => {})
  mql = window.matchMedia('(max-width: 767px)')
  isMobile.value = !!mql.matches
  mqlHandler = (e) => (isMobile.value = !!e.matches)
  mql.addEventListener?.('change', mqlHandler)

  tabletMql = window.matchMedia('(min-width: 768px) and (max-width: 1180px)')
  isTablet.value = !!tabletMql.matches
  tabletMqlHandler = (e) => (isTablet.value = !!e.matches)
  tabletMql.addEventListener?.('change', tabletMqlHandler)
  window.addEventListener(EDITOR_IMAGES_CHANGED_EVENT, onExternalEditorImagesChanged as EventListener)
  ensureProjectReady.then(() => refreshProjectImageCounts().catch(() => {}))
  nextTick(() => {
    void applyEntryUploadHandoff().catch(() => {
      entryUploadHandoffApplied = false
    })
  })
})

watch(() => project.projectCode.value, (code, prev) => {
  if (!code || code === prev) return
  refreshProjectImageCounts().catch(() => {})
  scheduleRefreshEditorTotalPrice()
})

onBeforeUnmount(() => {
  if (mql && mqlHandler) mql.removeEventListener?.('change', mqlHandler)
  if (tabletMql && tabletMqlHandler) tabletMql.removeEventListener?.('change', tabletMqlHandler)
  window.removeEventListener(EDITOR_IMAGES_CHANGED_EVENT, onExternalEditorImagesChanged as EventListener)
  if (totalPriceTimer) clearTimeout(totalPriceTimer)
})

/* -------------------- state -------------------- */
const selectedIds = ref<string[]>([])
const pendingPreselectIds = computed(() => selectedIds.value.length ? [] : preselectIds.value.filter(Boolean))
const hasSelection = computed(() => selectedIds.value.length > 0 || pendingPreselectIds.value.length > 0)
let totalPriceTimer: ReturnType<typeof setTimeout> | null = null

// Mobile overlay open-state (Desktop ignoriert das; dort ist Sidebar immer sichtbar)
const mobileOpen = ref(false)
const editorInitialLoading = ref(false)

const reloadFromDbUpdateRunning = ref(false)
const refreshPersistedSizeRunning = ref(false)
let sidebarSelectionHydrateTimer: ReturnType<typeof setTimeout> | null = null

const logoUrl = (() => { try { return getWidgetConfig().logoUrl || '' } catch { return '' } })()
const rootPanelRef = ref<{ scrollToCategory: (categoryId?: number | string | null) => void } | null>(null)
const forceAllOverview = ref(false)
const pendingScrollCategoryId = ref<number | null>(null)
const pendingAutoUploadCategoryId = ref<number | null>(null)
const pendingAutoUploadIncludeDescendants = ref(false)
const projectCategoryCounts = ref<Map<number, number>>(new Map())
const sessionRecordCache = ref<Record<string, ImageRecord>>({})
const categoryTree = ref<EditorCategoryTreeResponse | null>(null)
const catalogBootstrap = ref<EditorCatalogBootstrapResponse | null>(null)
let projectImageCountsPromise: Promise<void> | null = null

const sessionRecordsList = computed<ImageRecord[]>(() => Object.values(sessionRecordCache.value || {}))
const liveEditorRecordsForCounts = computed<ImageRecord[]>(() => {
  const raw = (miRef.value as any)?.images?.value ?? (miRef.value as any)?.images ?? []
  if (!Array.isArray(raw)) return []
  return raw
    .map((img: any) => (img?.record || null) as ImageRecord | null)
    .filter((rec: ImageRecord | null): rec is ImageRecord => !!rec)
})

const effectiveCategoryCounts = computed<Map<number, number>>(() => {
  if (projectCategoryCounts.value.size) return projectCategoryCounts.value
  const fallback = new Map<number, number>()
  for (const rec of liveEditorRecordsForCounts.value) {
    const categoryId = Number((rec as any)?.categoryId ?? 0)
    if (categoryId > 0) fallback.set(categoryId, (fallback.get(categoryId) ?? 0) + 1)
  }
  return fallback
})

async function loadCategoryTree() {
  const payload = await fetchEditorCatalogBootstrap({
    editorBaseUrl: getWidgetConfig().editorBaseUrl,
  })
  catalogBootstrap.value = payload
  categoryTree.value = payload.tree
}

async function loadProjectImageRecords(preferLocalOnly = false): Promise<ImageRecord[]> {
  project.loadFromStorage()
  const sessionCode = String(project.projectCode.value || await project.ensureProject().catch(() => '') || '').trim()
  const transient = sessionCode
    ? transientUploads.all.filter((rec: any) => {
        const recProjectCode = String(rec?.projectCode || '').trim()
        return recProjectCode === '' || recProjectCode === sessionCode
      })
    : transientUploads.all

  const editorBaseUrl = String(getWidgetConfig().editorBaseUrl || '').trim()
  const readLocalRecordsWithFallback = async () => {
    const fallback = new Promise<ImageRecord[]>((resolve) => {
      setTimeout(() => resolve([]), 600)
    })
    return Promise.race<ImageRecord[]>([
      db.getByProjectCode(sessionCode).catch(() => []),
      fallback,
    ]).catch(() => [])
  }
  if (preferLocalOnly) {
    if (sessionCode) {
      const local = await readLocalRecordsWithFallback()
      const merged = new Map<string, ImageRecord>()
      for (const rec of Object.values(sessionRecordCache.value || {})) merged.set(String((rec as any)?.id || ''), rec)
      for (const rec of local) merged.set(String((rec as any)?.id || ''), rec)
      for (const rec of transient) merged.set(String((rec as any)?.id || ''), rec)
      sessionRecordCache.value = Object.fromEntries([...merged.entries()])
      return [...merged.values()]
    }
    if (Object.keys(sessionRecordCache.value || {}).length) {
      const merged = new Map<string, ImageRecord>()
      for (const rec of Object.values(sessionRecordCache.value || {})) merged.set(String((rec as any)?.id || ''), rec)
      for (const rec of transient) merged.set(String((rec as any)?.id || ''), rec)
      return [...merged.values()]
    }
  }
  if (sessionCode && editorBaseUrl) {
    try {
      const local = await readLocalRecordsWithFallback()
      const items = await fetchProjectUploads({
        projectCode: sessionCode,
        editorBaseUrl,
        limit: 500,
        offset: 0,
      })
      const mapped = items.map((item) => mapRemoteUploadToRecordMeta(item, { projectCode: sessionCode }))
      const localIds = new Set(local.map((rec) => String((rec as any)?.id || '').trim()).filter(Boolean))
      const merged = new Map<string, ImageRecord>()
      for (const rec of mapped) merged.set(String((rec as any)?.id || ''), rec)
      for (const rec of local) {
        const id = String((rec as any)?.id || '')
        const current = merged.get(id)
        merged.set(id, current ? mergeServerAndLocalImageRecord(current, rec) : rec)
      }
      for (const rec of transient) merged.set(String((rec as any)?.id || ''), rec)
      const missingLocalServerRecords = mapped.filter((rec) => {
        const id = String((rec as any)?.id || '').trim()
        return !!id && !localIds.has(id)
      })
      if (missingLocalServerRecords.length) {
        void Promise.allSettled(
          missingLocalServerRecords.map((rec) => db.put(rec).catch(() => null)),
        )
      }
      sessionRecordCache.value = Object.fromEntries([...merged.entries()])
      return [...merged.values()]
    } catch {
      // Fallback for older backends / local dev without API.
    }
  }

  if (sessionCode) {
    const local = await readLocalRecordsWithFallback()
    const merged = new Map<string, ImageRecord>()
    for (const rec of local) merged.set(String((rec as any)?.id || ''), rec)
    for (const rec of transient) merged.set(String((rec as any)?.id || ''), rec)
    sessionRecordCache.value = Object.fromEntries([...merged.entries()])
    return [...merged.values()]
  }
  const local = await db.getAll()
  const merged = new Map<string, ImageRecord>()
  for (const rec of local) merged.set(String((rec as any)?.id || ''), rec)
  for (const rec of transient) merged.set(String((rec as any)?.id || ''), rec)
  sessionRecordCache.value = Object.fromEntries([...merged.entries()])
  return [...merged.values()]
}

async function refreshProjectImageCounts() {
  if (projectImageCountsPromise) return projectImageCountsPromise

  const request = (async () => {
    let records = await loadProjectImageRecords(true)
    if (!records.length && String(project.projectCode.value || '').trim() && String(getWidgetConfig().editorBaseUrl || '').trim()) {
      records = await loadProjectImageRecords(false)
    }

    const byCategory = new Map<number, number>()
    for (const rec of records) {
      const categoryId = Number((rec as any)?.categoryId ?? 0)
      if (categoryId > 0) byCategory.set(categoryId, (byCategory.get(categoryId) ?? 0) + 1)
    }
    projectCategoryCounts.value = byCategory
  })()

  projectImageCountsPromise = request.finally(() => {
    if (projectImageCountsPromise === request) projectImageCountsPromise = null
  })

  return projectImageCountsPromise
}

function directImageCountForCategory(catId?: number | null) {
  const id = Number(catId || 0)
  return id > 0 ? (effectiveCategoryCounts.value.get(id) ?? 0) : 0
}

function totalImageCountForRoot(catId?: number | null) {
  const id = Number(catId || 0)
  if (!(id > 0)) return 0
  let total = 0
  for (const [categoryId, count] of effectiveCategoryCounts.value.entries()) {
    const cid = Number(categoryId || 0)
    if (!(cid > 0)) continue
    const rootId = Number(categoryRootMap.value.get(cid) ?? cid ?? 0)
    if (cid === id || rootId === id) total += Number(count || 0)
  }
  return total
}

async function countProjectImagesForCategory(catId?: number | null, includeDescendants = false) {
  const id = Number(catId || 0)
  if (!(id > 0)) return 0

  const records = await loadProjectImageRecords(true)
  let count = 0

  for (const rec of records) {
    const categoryId = Number((rec as any)?.categoryId ?? 0)
    if (!(categoryId > 0)) continue

    const inferredRootId = Number(
      (rec as any)?.parentCategoryId
      ?? categoryRootMap.value.get(categoryId)
      ?? categoryId
      ?? 0,
    )

    if (includeDescendants) {
      if (categoryId === id || inferredRootId === id) count += 1
      continue
    }

    if (categoryId === id) count += 1
  }

  return count
}

function onExternalEditorImagesChanged(event?: Event) {
  const detail = (event as CustomEvent<any> | undefined)?.detail || {}
  const insertedRecords = Array.isArray(detail?.records) ? detail.records : []
  if (insertedRecords.length) {
    const nextCache = { ...(sessionRecordCache.value || {}) } as Record<string, ImageRecord>
    let changed = false
    for (const rec of insertedRecords) {
      const id = String((rec as any)?.id || '').trim()
      if (!id) continue
      nextCache[id] = rec as ImageRecord
      changed = true
    }
    if (changed) sessionRecordCache.value = nextCache
  }
  if (String(detail?.action || '') === 'deleted') {
    const deletedIds = new Set(
      (Array.isArray(detail?.ids) ? detail.ids : [])
        .map((id: any) => String(id || '').trim())
        .filter(Boolean),
    )
    if (deletedIds.size) {
      const nextCache: Record<string, ImageRecord> = {}
      for (const [id, rec] of Object.entries(sessionRecordCache.value || {})) {
        if (deletedIds.has(String(id || '').trim())) continue
        nextCache[id] = rec
      }
      sessionRecordCache.value = nextCache
    }
    invalidateProjectUploadsCache({
      editorBaseUrl: String(getWidgetConfig().editorBaseUrl || '').trim(),
      projectCode: String(project.projectCode.value || '').trim(),
    })
  }
  refreshProjectImageCounts().catch(() => {})
  scheduleRefreshEditorTotalPrice()
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value || 0))
}

function buildPriceRequestFromRecord(rec: any): (PriceRequest & { key?: string }) | null {
  const preset = rec?.edits?.preset || {}
  const productId = Number(rec?.productId ?? preset?.productId ?? 0)
  if (!(productId > 0)) return null
  const quantity = Math.max(1, Number(preset?.quantity ?? 1))
  const sizeSource = preset?.size || null
  const sizeEntityId = String(sizeSource?.id ?? '').trim()
  const widthMm = Number(sizeSource?.widthMm || 0) || undefined
  const heightMm = Number(sizeSource?.heightMm || 0) || undefined
  const size = sizeSource && (sizeEntityId || widthMm || heightMm)
    ? {
        ...(sizeEntityId ? { id: sizeEntityId } : {}),
        widthMm,
        heightMm,
      }
    : undefined

  const variants = [
    ...((Array.isArray(preset?.options) ? preset.options : [])
      .map((o: any) => ({
        groupId: String(o?.groupId || '').trim(),
        optionId: String(o?.variantId || '').trim(),
      }))
      .filter((o: any) => o.groupId && o.optionId)),
  ]

  if (!variants.length && !size) return null

  return {
    key: String(rec?.id || ''),
    productId,
    categoryId: Number(rec?.categoryId ?? 0) || undefined,
    quantity,
    ...(sizeEntityId ? { sizeId: sizeEntityId } : {}),
    size,
    variants,
  }
}

function livePriceItemsReady(items: Array<PriceRequest & { key?: string }>) {
  if (!items.length) return true
  return items.every((item: any) => {
    const hasVariants = Array.isArray(item?.variants) && item.variants.length > 0
    const hasSizeId = !!String(item?.sizeId || '').trim()
    const hasSizeDims = Number(item?.size?.widthMm || 0) > 0 && Number(item?.size?.heightMm || 0) > 0
    if (!hasVariants) return true
    return hasSizeId || hasSizeDims
  })
}

async function refreshEditorTotalPrice() {
  if (totalPriceRefreshRunning) {
    totalPriceRefreshQueued = true
    return
  }
  totalPriceRefreshRunning = true
  pendingTotalPriceRefresh = false
  const reqToken = ++totalPriceReqToken

  try {
    const liveItems = miRef.value?.bulkPriceRequestsForCurrentImages?.() || []
    const items = liveItems.filter((x): x is PriceRequest & { key?: string } => !!x)
    const liveImageCount = Number((miRef.value as any)?.images?.value?.length ?? (miRef.value as any)?.images?.length ?? 0)
    if (reqToken !== totalPriceReqToken) return

    if (items.length && !livePriceItemsReady(items)) {
      pendingTotalPriceRefresh = true
      totalPriceRefreshQueued = true
      return
    }

    if (!items.length) {
      if (liveImageCount > 0) {
        pendingTotalPriceRefresh = true
        totalPriceRefreshQueued = true
        return
      }
      lastQuotedItemsHash = ''
      editorTotalPriceLabel.value = ''
      return
    }

    const nextHash = JSON.stringify(items)
    if (nextHash === lastQuotedItemsHash) return
    lastQuotedItemsHash = nextHash

    const result = await quoteBulk(items)
    if (reqToken !== totalPriceReqToken) return
    editorTotalPriceLabel.value = result?.total > 0 ? formatMoney(result.total) : ''
  } catch {
    if (reqToken !== totalPriceReqToken) return
    lastQuotedItemsHash = ''
    editorTotalPriceLabel.value = ''
  } finally {
    totalPriceRefreshRunning = false
    if (totalPriceRefreshQueued || pendingTotalPriceRefresh) {
      totalPriceRefreshQueued = false
      scheduleRefreshEditorTotalPrice(180)
    }
  }
}

function currentUploadActiveCount(): number {
  const stats: any = miRef.value?.uploadStats
  const raw = stats?.value ?? stats ?? {}
  return Math.max(0, Number(raw?.active || 0))
}

function scheduleRefreshEditorTotalPrice(delay = 120) {
  if (totalPriceTimer) clearTimeout(totalPriceTimer)
  totalPriceTimer = setTimeout(() => {
    totalPriceTimer = null
    void refreshEditorTotalPrice()
  }, Math.max(0, Number(delay || 0)))
}

/* -------------------- route params -------------------- */
const { categoryId, productId } = useRouteQueryIds()

const preselectIds = computed<string[]>(() => {
  const single = String(Array.isArray(route.query.imageId) ? route.query.imageId[0] : route.query.imageId || '').trim()
  if (single) return [single]
  const raw = route.query.imageIds
  const s = String(Array.isArray(raw) ? raw[0] : raw || '')
  return s ? s.split(',').map((x) => x.trim()).filter(Boolean) : []
})

const cropEngine = computed<'cropper' | 'canvas'>(() => {
  const raw = String(route.query.cropEngine || route.query.engine || '').toLowerCase()
  return raw === 'canvas' ? 'canvas' : 'cropper'
})

/* -------------------- product config -------------------- */
const currentProduct = computed<any | null>(() => {
  const id = Number(productId.value || 0)
  if (!(id > 0)) return null
  return catalogBootstrap.value?.products?.[String(id)] ?? null
})

const mappedCfg = computed(() => {
  return currentProduct.value?.configurator ?? null
})

const productName = computed<string>(() => {
  const p: any = currentProduct.value
  // Kein "Produkt #0"-Platzhalter: ohne geladenes Produkt lieber leer, der Titel
  // wird dann aus dem Katalog bzw. serverseitig aus der Produkt-ID aufgeloest.
  return String(p?.title || p?.name || '')
})

const sizeOptions = computed(() => {
  const groups = mappedCfg.value?.groups || []
  const sizeGroup = groups.find((g: any) => g.isSize)
  return sizeGroup?.sizeOptions || []
})

/* -------------------- aktuelle Kategorie laden (für Parent-Info) -------------------- */
const currentCategory = computed<any | null>(() => {
  const id = Number(categoryId.value || 0)
  if (!(id > 0)) return null
  return allVisibleCategories.value.find((cat: any) => Number(cat?._id) === id) ?? null
})

// Kategoriename: direkt aus der geladenen Kategorie (funktioniert auch ohne productId in URL)
const categoryLabel = computed<string>(() => {
  const fromCat = (currentCategory.value as any)?.title
  if (fromCat) return fromCat
  // Fallback: aus der Produktkategorieliste
  const cats: any[] = currentProduct.value?.categories ?? []
  const match = cats.find((cat: any) => Number(cat?._id) === categoryId.value)
  return match?.title || ''
})

function isRegularEditorCategory(category: any): boolean {
  if (!category || category?.showInEditor === false) return false
  return resolvePrintboxFamilyId(category) === null
}

// Wenn aktuelle Kategorie eine Unterkategorie ist, liefert parent._id die Root-ID
const parentCategoryId = computed<number | null>(() => {
  const p = (currentCategory.value as any)?.parent
  return p ? Number(p._id) : null
})

// Linkes Panel: Root-Kategorie hervorheben (pending nav überschreibt aktuelle Selektion)
const activeCategoryId = computed<number | null>(() =>
  forceAllOverview.value ? null : (pendingNavCatId.value ?? selectionRootCatId2.value ?? parentCategoryId.value ?? categoryId.value)
)

const allVisibleCategories = computed<EditorCategoryTreeItem[]>(() =>
  Array.isArray(categoryTree.value?.items) ? categoryTree.value!.items : [],
)

/* -------------------- Root-Kategorien (linkes Panel) -------------------- */
const rootCategories = computed(() =>
  (Array.isArray(categoryTree.value?.roots) ? categoryTree.value!.roots : [])
    .filter((c: any) => isRegularEditorCategory(c))
)

const categoryRootMap = computed<Map<number, number>>(() => {
  const map = new Map<number, number>()
  const byId = new Map<number, EditorCategoryTreeItem>()
  for (const cat of allVisibleCategories.value) {
    const id = Number(cat?._id ?? 0)
    if (!(id > 0)) continue
    byId.set(id, cat)
  }

  for (const cat of allVisibleCategories.value) {
    const id = Number(cat?._id ?? 0)
    if (!(id > 0)) continue
    let rootId = id
    let parentId = Number(cat?.parentId ?? cat?.parent?._id ?? 0)
    const visited = new Set<number>([id])
    while (parentId > 0 && !visited.has(parentId)) {
      visited.add(parentId)
      rootId = parentId
      const parent = byId.get(parentId)
      parentId = Number(parent?.parentId ?? parent?.parent?._id ?? 0)
    }
    map.set(id, rootId)
  }
  return map
})

const rootCategoryChildIdsMap = computed<Map<number, number[]>>(() => {
  const map = new Map<number, number[]>()
  for (const root of rootCategories.value) {
    const rootId = Number(root?._id ?? 0)
    if (!(rootId > 0)) continue
    const childIds = Array.isArray((root as any)?.childIds)
      ? (root as any).childIds.map((id: any) => Number(id || 0)).filter((id: number) => id > 0)
      : []
    map.set(rootId, childIds)
  }
  return map
})

const rootCategoryDescendantIdsMap = computed<Map<number, number[]>>(() => {
  const map = new Map<number, number[]>()
  for (const root of rootCategories.value) {
    const rootId = Number(root?._id ?? 0)
    if (!(rootId > 0)) continue
    const descendantIds = Array.isArray((root as any)?.descendantIds)
      ? (root as any).descendantIds.map((id: any) => Number(id || 0)).filter((id: number) => id > 0)
      : []
    map.set(rootId, descendantIds)
  }
  return map
})

// editorType-Map: categoryId → 'single' | 'multi'
const categoryEditorTypes = computed<Record<number, 'single' | 'multi'>>(() => {
  const map: Record<number, 'single' | 'multi'> = {}
  for (const c of allVisibleCategories.value.filter((c: any) => isRegularEditorCategory(c))) {
    const id = Number(c?._id ?? 0)
    if (!(id > 0)) continue

    const ownType = c?.editorType === 'single' ? 'single' : (c?.editorType === 'multi' ? 'multi' : null)
    const parentType = c?.parent?.editorType === 'single' ? 'single' : (c?.parent?.editorType === 'multi' ? 'multi' : null)

    map[id] = ownType || parentType || 'multi'
  }
  return map
})

const categoryEditorConfigs = computed<Record<number, any>>(() => {
  const map: Record<number, any> = {}
  for (const c of allVisibleCategories.value.filter((c: any) => isRegularEditorCategory(c))) {
    const id = Number(c?._id ?? 0)
    if (!(id > 0)) continue
    map[id] = c?.editorConfig ?? {}
  }

  return map
})

// 3D-Preview-Config für das aktuell gewählte Produkt/Kategorie
const previewModelConfig = computed(() => {
  const productCfg = (currentProduct.value as any)?.editorOverrideConfig ?? null
  const categoryCfg = (currentCategory.value as any)?.editorConfig ?? null
  return resolvePreview3dOverrides({ categoryConfig: categoryCfg, productConfig: productCfg })
})

/* -------------------- Unterkategorien (horizontaler Strip) -------------------- */
// Unterkategorie-Parent: ist Parent der aktuellen Kategorie (falls Subkat) oder die Kategorie selbst (falls Root)
const subcatParentId = computed<number | null>(() => {
  if (parentCategoryId.value) return Number(parentCategoryId.value || 0) || null
  if (categoryId.value) return Number(categoryId.value || 0) || null
  return null
})
const subCategories = computed(() =>
  allVisibleCategories.value
    .filter((c: any) => Number(c?.parentId ?? 0) === Number(subcatParentId.value ?? 0))
    .filter((c: any) => isRegularEditorCategory(c))
)

/* -------------------- Pending category navigation (Bild verschieben) -------------------- */
// Wenn User mit Auswahl auf andere Root-Kategorie klickt → Sidebar lädt Inhalte dieser Kategorie
const pendingNavCatId = ref<number | null>(null)

const pendingSubCategories = computed(() =>
  allVisibleCategories.value
    .filter((c: any) => Number(c?.parentId ?? 0) === Number(pendingNavCatId.value ?? 0))
    .filter((c: any) => isRegularEditorCategory(c))
    .map((c: any) => ({ ...c, productCount: null }))
)

// Selektiertes Bild: Kategorie + Produkt aus dem DB-Record lesen
const selectionFirstRecord = computed(() => {
  if (!selectedIds.value.length) return null
  const m = miRef.value as any
  const img = m?.firstSelectedImage?.value ?? m?.firstSelectedImage
  return (img?.record as any) ?? null
})
const selectionCatId     = computed(() => Number(selectionFirstRecord.value?.categoryId ?? 0) || null)
const selectionParentId  = computed(() => {
  const explicitParentId = Number(selectionFirstRecord.value?.parentCategoryId ?? 0) || null
  if (explicitParentId) return explicitParentId
  const catId = Number(selectionFirstRecord.value?.categoryId ?? 0) || null
  if (!catId) return null
  const rootId = Number(categoryRootIdsMap.value[catId] ?? 0) || null
  return rootId && rootId !== catId ? rootId : null
})
const selectionProductId = computed(() => Number(selectionFirstRecord.value?.productId ?? 0) || null)
// Root-Kategorie des selektierten Bildes (für Subkat-Query)
const selectionRootCatId2 = computed(() => selectionParentId.value ?? selectionCatId.value)

// Subkategorien der Root-Kategorie des selektierten Bildes laden
const selectionSubCategories = computed(() =>
  allVisibleCategories.value
    .filter((c: any) => Number(c?.parentId ?? 0) === Number(selectionRootCatId2.value ?? 0))
    .filter((c: any) => isRegularEditorCategory(c))
    .map((c: any) => ({ ...c, productCount: null }))
)

// Effektive Werte für Sidebar: pending > selection > URL-Werte
const sidebarCategoryId = computed(() => {
  if (pendingNavCatId.value) return pendingNavCatId.value
  if (hasSelection.value && selectionRootCatId2.value) return selectionRootCatId2.value
  return categoryId.value
})
const sidebarProductId = computed(() => {
  if (pendingNavCatId.value) return null
  if (hasSelection.value && selectionProductId.value) return selectionProductId.value
  return productId.value
})
const sidebarSubcats = computed(() => {
  if (pendingNavCatId.value) return pendingSubCategories.value
  if (hasSelection.value && selectionSubCategories.value.length) return selectionSubCategories.value
  return subCategoriesWithCounts.value
})
const sidebarActiveSubcatId = computed(() => {
  if (pendingNavCatId.value) return null
  if (hasSelection.value) return selectionParentId.value ? selectionCatId.value : null
  return parentCategoryId.value ? categoryId.value : null
})
const sidebarParentCatId = computed(() => {
  if (pendingNavCatId.value) return null
  if (hasSelection.value) return selectionParentId.value
  return parentCategoryId.value
})
const sidebarStateKey = computed(() => {
  const selKey = selectedIds.value.join(',') || 'none'
  const rootKey = Number(sidebarCategoryId.value || 0)
  const subKey = Number(sidebarActiveSubcatId.value || 0)
  const productKey = Number(sidebarProductId.value || 0)
  const pendingKey = Number(pendingNavCatId.value || 0)
  return `sidebar:${selKey}:${rootKey}:${subKey}:${productKey}:${pendingKey}`
})

// Pending verwerfen wenn Auswahl aufgehoben wird
watch(hasSelection, (has) => { if (!has) pendingNavCatId.value = null })
watch(() => route.query.category ?? route.query.categoryId, (cid) => {
  if (cid) forceAllOverview.value = false
})
watch(() => selectedIds.value.join('|'), () => {
  if (!selectedIds.value.length) forceAllOverview.value = false
})
watch(
  [pendingScrollCategoryId, () => rootCategories.value.length],
  async () => {
    const cid = pendingScrollCategoryId.value
    if (!cid) return
    await Promise.resolve()
    rootPanelRef.value?.scrollToCategory(cid)
    pendingScrollCategoryId.value = null
  },
)

// Titel-Lookup für alle bekannten Kategorien (Root + Sub) → für Tile-Gruppen-Header
const categoryTitlesMap = computed<Record<number, string>>(() => {
  const map: Record<number, string> = {}
  for (const c of allVisibleCategories.value) {
    const id = Number(c?._id ?? 0)
    if (id > 0 && c?.title) map[id] = String(c.title)
    const parentId = Number(c?.parentId ?? c?.parent?._id ?? 0)
    if (parentId > 0 && c?.parent?.title) map[parentId] = String(c.parent.title)
  }
  return map
})

const categoryRootIdsMap = computed<Record<number, number>>(() => {
  const map: Record<number, number> = {}
  for (const [categoryId, rootId] of categoryRootMap.value.entries()) {
    if (categoryId > 0 && rootId > 0) map[categoryId] = rootId
  }
  return map
})

const rootCategoryTitlesMap = computed<Record<number, string>>(() => {
  const map: Record<number, string> = {}
  for (const root of rootCategories.value) {
    const rootId = Number(root?._id ?? 0)
    const title = String(root?.title || '').trim()
    if (rootId > 0 && title) map[rootId] = title
  }
  return map
})

const rootCategoryOrderMap = computed<Record<number, number>>(() => {
  const map: Record<number, number> = {}
  rootCategories.value.forEach((root: any, index: number) => {
    const id = Number(root?._id ?? 0)
    if (id > 0) map[id] = index
  })
  return map
})

const categoryOrderMap = computed<Record<number, number>>(() => {
  const map: Record<number, number> = {}
  allVisibleCategories.value
    .filter((c: any) => isRegularEditorCategory(c))
    .forEach((c: any, index: number) => {
      const id = Number(c?._id ?? 0)
      if (id > 0) map[id] = index
    })
  return map
})

const categoryThumbnailsMap = computed<Record<number, string>>(() => {
  const map: Record<number, string> = {}
  for (const c of allVisibleCategories.value) {
    const id = Number(c?._id ?? 0)
    if (!(id > 0)) continue
    const thumb = String(c?.thumbnailWebp || c?.thumbnail || '').trim()
    if (thumb) map[id] = thumb
  }
  return map
})

watch(
  [() => categoryRootMap.value.size, () => rootCategories.value.length, () => subCategories.value.length],
  ([rootMapSize, rootCount]) => {
    if (!rootMapSize && !rootCount) return
    refreshProjectImageCounts().catch(() => {})
  },
  { immediate: true },
)

const rootCategoriesWithCounts = computed(() => {
  return rootCategories.value.map((c: any) => ({
    ...c,
    childIds: rootCategoryChildIdsMap.value.get(Number(c?._id || 0)) || [],
    descendantIds: rootCategoryDescendantIdsMap.value.get(Number(c?._id || 0)) || [],
    productCount: totalImageCountForRoot(Number(c?._id || 0)),
  }))
})

// Alle Unterkategorien; Badge = Bildanzahl dieser Unterkategorie im Projekt
const subCategoriesWithCounts = computed(() =>
  subCategories.value.map(c => ({
    ...c,
    productCount: directImageCountForCategory(Number(c._id)),
  }))
)

// Erlaubte Kategorie-IDs für ImageTool-Filter
const filterCategoryIds = computed<number[]>(() => {
  if (!categoryId.value) return []
  // Unterkategorie gewählt → nur diese
  if (parentCategoryId.value) return [categoryId.value]
  // Root gewählt → Root + alle Unterkategorien
  const ids: number[] = [categoryId.value]
  for (const sub of subCategories.value) {
    const id = Number(sub._id)
    if (id > 0) ids.push(id)
  }
  return ids
})

const priorityCategoryIds = computed<number[]>(() => {
  const unique = (ids: number[]) => ids.filter((id, idx, arr) => id > 0 && arr.indexOf(id) === idx)
  if (selectedIds.value.length === 1 && selectionRootCatId2.value) {
    const rootId = Number(selectionRootCatId2.value || 0)
    return unique([rootId, ...(rootCategoryDescendantIdsMap.value.get(rootId) || [])])
  }

  if (pendingNavCatId.value) {
    const rootId = Number(pendingNavCatId.value || 0)
    return unique([rootId, ...(rootCategoryDescendantIdsMap.value.get(rootId) || [])])
  }

  if (parentCategoryId.value) {
    const rootId = Number(parentCategoryId.value || 0)
    return unique([rootId, ...(rootCategoryDescendantIdsMap.value.get(rootId) || [])])
  }

  if (categoryId.value) {
    const rootId = Number(categoryId.value || 0)
    return unique([rootId, ...(rootCategoryDescendantIdsMap.value.get(rootId) || [])])
  }

  return []
})

function onRootCategorySelect(cat: any) {
  // "Alle"-Button → kein Kategorie-Filter
  if (!cat) {
    pendingNavCatId.value = null
    forceAllOverview.value = true
    const q = { ...route.query as any }
    delete q.categoryId
    delete q.category
    delete q.productId
    delete q.product
    router.push({ query: q })
    return
  }
  if (hasSelection.value) {
    forceAllOverview.value = false
    // Different root category clicked → load that category in sidebar so user can move image there
    const visibleRootId = Number(activeCategoryId.value || selectionRootCatId2.value || parentCategoryId.value || categoryId.value || 0)
    if (Number(cat._id) !== visibleRootId) {
      pendingNavCatId.value = Number(cat._id)
    } else if (pendingNavCatId.value && Number(cat._id) === Number(selectionRootCatId2.value || parentCategoryId.value || categoryId.value || 0)) {
      pendingNavCatId.value = Number(cat._id)
    }
    openSidebar()
  } else {
    pendingNavCatId.value = null
    forceAllOverview.value = false
    pendingAutoUploadIncludeDescendants.value = true
    pendingAutoUploadCategoryId.value = totalImageCountForRoot(Number(cat?._id)) === 0 ? Number(cat._id) : null
    const q = { ...route.query as any }
    q.category = String(cat._id)
    q.categoryId = String(cat._id)
    delete q.product
    delete q.productId
    router.push({ query: q })
  }
}

function onSubCategorySelect(cat: any) {
  forceAllOverview.value = false
  pendingAutoUploadIncludeDescendants.value = false
  pendingAutoUploadCategoryId.value = directImageCountForCategory(Number(cat?._id)) === 0 ? Number(cat._id) : null
  const q = { ...route.query as any }
  q.category = String(cat._id)
  q.categoryId = String(cat._id)
  delete q.product
  delete q.productId
  router.push({ query: q })
}

/* -------------------- selection + sidebar open/close -------------------- */
function selectIds(ids: string[]) {
  const nextIds = Array.isArray(ids) ? ids : []
  if (!nextIds.length && pendingPreselectIds.value.length) return
  const prevKey = selectedIds.value.join('|')
  const nextKey = nextIds.join('|')
  if (prevKey === nextKey) return
  selectedIds.value = nextIds
  if (route.query.imageId || route.query.imageIds) {
    const q = { ...route.query as any }
    delete q.imageId
    delete q.imageIds
    router.replace({ query: q }).catch(() => {})
  }
  if (selectedIds.value.length) {
    nextTick(() => {
      if (isMobile.value) openSidebar()
      if (sidebarSelectionHydrateTimer) clearTimeout(sidebarSelectionHydrateTimer)
      if (selectedIds.value.length === 1) {
        sidebarSelectionHydrateTimer = setTimeout(() => {
          sidebarSelectionHydrateTimer = null
          Promise.resolve(sidebarRef.value?.refreshPersistedSize?.()).catch(() => {})
        }, 120)
      }
    })
  }
  // Wenn Auswahl auf Mobile wegfällt, Sidebar schließen
  if ((isMobile.value || isTablet.value) && !selectedIds.value.length) mobileOpen.value = false
}

function openSidebar() {
  // Desktop: Sidebar ist eh sichtbar -> nichts togglen
  if (!isMobile.value && !isTablet.value) return
  // Mobile/Tablet: nur öffnen, wenn Auswahl vorhanden
  if (!hasSelection.value) return
  mobileOpen.value = true
}

function onEditorInitialLoading(loading: boolean) {
  editorInitialLoading.value = !!loading
}

function closeSidebar() {
  if (!isMobile.value && !isTablet.value) return
  pendingNavCatId.value = null
  mobileOpen.value = false
}

/* -------------------- ImageTool API -------------------- */
type MiApi = {
  images?: any
  uploadStats?: { value?: { active?: number } } | { active?: number }
  applySidebarSizeLocally?: (ids: string[], size: any) => Promise<void> | void
  reload?: (...args: any[]) => Promise<void> | void
  revealCategoryGroup?: (categoryId?: number | string | null, behavior?: ScrollBehavior) => Promise<void> | void
  clearSelection?: () => void
  openUploadPicker?: (categoryId?: number, productId?: number) => void
  openUploadPrompt?: (categoryId?: number, productId?: number) => void
  importEntryBufferedUploads?: (files: any[], payload: any) => Promise<void>
  countImagesInCurrentView?: () => Promise<number>
  bulkPriceRequestsForCurrentImages?: () => Array<PriceRequest & { key?: string }>
}
const miRef = ref<MiApi | null>(null)
const sidebarRef = ref<{ refreshPersistedSize: () => Promise<void> } | null>(null)
const skipNextSidebarSizeReloadIds = new Set<string>()
const sidebarTotalPriceLabel = ref('')
const editorTotalPriceLabel = ref('')
let totalPriceReqToken = 0
let pendingTotalPriceRefresh = false
let lastQuotedItemsHash = ''
let totalPriceRefreshQueued = false
let totalPriceRefreshRunning = false
let entryUploadHandoffApplied = false

async function applyEntryUploadHandoff() {
  if (entryUploadHandoffApplied) return
  if (!miRef.value?.importEntryBufferedUploads) return
  const handoff = entryUploadHandoff.peek()
  if (!handoff?.files?.length) return
  entryUploadHandoffApplied = true
  entryUploadHandoff.consume()
  await miRef.value.importEntryBufferedUploads(handoff.files, handoff.payload)
}

watch(
  () => !!miRef.value?.importEntryBufferedUploads,
  (ready) => {
    if (!ready) return
    void applyEntryUploadHandoff().catch(() => {
      entryUploadHandoffApplied = false
    })
  },
)

const totalImageCount = computed(() => {
  const m = miRef.value as any
  return m?.images?.value?.length ?? m?.images?.length ?? 0
})

watch(totalImageCount, () => {
  scheduleRefreshEditorTotalPrice()
}, { immediate: true })

watch(
  () => currentUploadActiveCount(),
  (active, prev) => {
    if (active <= 0 && (prev ?? 0) > 0 && pendingTotalPriceRefresh) {
      scheduleRefreshEditorTotalPrice(50)
    }
  },
)

async function patchSessionCacheFromDb(ids: string[]) {
  const wanted = (ids || []).map((id) => String(id || '').trim()).filter(Boolean)
  if (!wanted.length) return
  const rows = await Promise.all(wanted.map((id) => db.get(id).catch(() => null)))
  const next = { ...(sessionRecordCache.value || {}) } as Record<string, ImageRecord>
  let changed = false
  rows.forEach((rec: any, index) => {
    const id = wanted[index]
    if (!id || !rec) return
    next[id] = rec as ImageRecord
    changed = true
  })
  if (changed) sessionRecordCache.value = next
}

function onDbUpdated(ids: string[]) {
  if (reloadFromDbUpdateRunning.value) return
  reloadFromDbUpdateRunning.value = true
  const touched = (ids || []).map((id) => String(id || '').trim()).filter(Boolean)
  const skipReload = touched.length > 0 && touched.every((id) => skipNextSidebarSizeReloadIds.has(id))
  if (skipReload) {
    for (const id of touched) skipNextSidebarSizeReloadIds.delete(id)
  }
  Promise.resolve(patchSessionCacheFromDb(ids))
    .catch(() => {})
    .then(() => refreshProjectImageCounts().catch(() => {}))
    .then(() => skipReload ? undefined : Promise.resolve(miRef.value?.reload?.(touched)))
    .then(() => {
      const categoryIds = [...new Set(
        touched
          .map((id) => Number(sessionRecordCache.value?.[id]?.categoryId ?? 0) || 0)
          .filter((id) => id > 0),
      )]
      const targetCategoryId = categoryIds[categoryIds.length - 1] || null
      if (!targetCategoryId) return
      return Promise.resolve(miRef.value?.revealCategoryGroup?.(targetCategoryId, 'smooth'))
    })
    .catch(() => {})
    .finally(() => {
      reloadFromDbUpdateRunning.value = false
    })
  scheduleRefreshEditorTotalPrice()
}

function onSidebarApplySizeLocal(payload: { ids: string[]; size: any }) {
  const touched = (payload?.ids || []).map((id) => String(id || '').trim()).filter(Boolean)
  const size = payload?.size || null
  if (!touched.length || !size) return
  for (const id of touched) skipNextSidebarSizeReloadIds.add(id)
  Promise.resolve(miRef.value?.applySidebarSizeLocally?.(touched, size))
    .catch(() => {})
  scheduleRefreshEditorTotalPrice(50)
}

function onMetaChanged(ids: string[]) {
  const touched = (ids || []).map((id) => String(id || '').trim()).filter(Boolean)
  // Tile hat Größe/Option geändert und in DB gespeichert.
  // Kein sofortiger Tile-Reload mehr: der lokale Tile-Zustand ist bereits korrekt
  // und ein nachgelagerter Reload kann stale Zwischenstände wieder sichtbar machen.
  Promise.resolve(patchSessionCacheFromDb(touched))
    .catch(() => {})
  if (!refreshPersistedSizeRunning.value) {
    refreshPersistedSizeRunning.value = true
    Promise.resolve(sidebarRef.value?.refreshPersistedSize?.())
      .catch(() => {})
      .finally(() => {
        refreshPersistedSizeRunning.value = false
      })
  }
  scheduleRefreshEditorTotalPrice()
}

watch(
  [
    pendingAutoUploadCategoryId,
    pendingAutoUploadIncludeDescendants,
    categoryId,
    productId,
  ],
  async () => {
    const cid = pendingAutoUploadCategoryId.value
    if (!cid || Number(categoryId.value || 0) !== Number(cid)) return
    await nextTick()
    const count = await countProjectImagesForCategory(cid, pendingAutoUploadIncludeDescendants.value)
    if (Number(count || 0) <= 0) {
      miRef.value?.openUploadPrompt?.(cid, productId.value || undefined)
    }
    pendingAutoUploadCategoryId.value = null
    pendingAutoUploadIncludeDescendants.value = false
  },
)

// Auswahl geändert (auch Abwählen ohne Übernehmen): Options-Vorschau verwerfen
watch(() => selectedIds.value.join('|'), () => {
  ;(miRef.value as any)?.setOptionPreview?.([], null)
})

function onSidebarOptionsPreview(payload: { ids: string[]; options: any[] } | null) {
  ;(miRef.value as any)?.setOptionPreview?.(payload?.ids || [], payload?.options || null)
}

function onSidebarApplied() {
  ;(miRef.value as any)?.setOptionPreview?.([], null)
  selectedIds.value = []
  miRef.value?.clearSelection?.()
  if (route.query.imageId || route.query.imageIds) {
    const q = { ...route.query as any }
    delete q.imageId
    delete q.imageIds
    router.replace({ query: q }).catch(() => {})
  }
  refreshProjectImageCounts().catch(() => {})
  scheduleRefreshEditorTotalPrice()
  if (isMobile.value || isTablet.value) closeSidebar()
}

async function onSidebarNavigate(payload: { categoryId?: number | null; productId?: number | null }) {
  const wasMoving = !!pendingNavCatId.value
  if (!wasMoving) return
  const movedCount = selectedIds.value.length
  pendingNavCatId.value = null
  const q: Record<string, string> = { ...route.query as any }
  if (wasMoving) {
    forceAllOverview.value = true
    pendingScrollCategoryId.value = payload.categoryId ? Number(payload.categoryId) : null
    // Strip preselect when moving images to a new category — prevents stale selectionRootCatId
    delete q.imageIds
    delete q.imageId
    delete q.categoryId
    delete q.category
    delete q.productId
    delete q.product
    // Toast: find the category name from root categories
    const catName = categoryTitlesMap.value[Number(payload.categoryId || 0)]
      ?? (payload.categoryId ? `Kategorie ${payload.categoryId}` : 'neue Kategorie')
    toast.success(`Bild${movedCount !== 1 ? 'er' : ''} wurde${movedCount !== 1 ? 'n' : ''} „${catName}" zugeordnet`)
  } else {
    forceAllOverview.value = false
    if (payload.categoryId) {
      q.categoryId = String(payload.categoryId)
      q.category = String(payload.categoryId)
    }
    if (payload.productId) {
      q.productId = String(payload.productId)
      q.product = String(payload.productId)
    }
  }
  await router.push({ query: q })
  if (wasMoving) {
    selectedIds.value = []
    if (isMobile.value || isTablet.value) mobileOpen.value = false
  }
  await nextTick()
  await Promise.resolve(miRef.value?.reload?.()).catch(() => {})
  await refreshProjectImageCounts().catch(() => {})
  scheduleRefreshEditorTotalPrice()
}

async function onSidebarAddToCart() {
  try {
    await Promise.resolve((miRef.value as any)?.addAllToCart?.())
  } catch (e: any) {
    toast.error(e?.message || 'Konnte nicht in den Warenkorb legen.')
  }
}

function onImageToolNavigate(payload: { categoryId: number; productId: number }) {
  const q: Record<string, string> = { ...route.query as any }
  q.categoryId = String(payload.categoryId)
  q.category   = String(payload.categoryId)
  q.productId  = String(payload.productId)
  q.product    = String(payload.productId)
  router.push({ query: q })
}
</script>

<style scoped>
.layout {
  min-height: 100vh;
  min-height: 100dvh;
  overflow: visible;
  background: #fff;
  color: #0f172a;
  width: 100%;
  max-width: 100vw;
}

/* ── Desktop: Kategorien | Sidebar | Main ─────────────── */
.layout {
  position: relative;
}

.layout--embedded-tile {
  min-height: 0;
  min-height: auto;
  max-width: 100%;
  overflow: hidden;
  border-radius: 16px;
}

.layout--embedded-tile .main {
  padding: 0;
  min-width: 0;
}

.editor-page-loading{
  position: absolute;
  inset: 0;
  z-index: 80;
  display: block;
  padding: 24px;
  background: linear-gradient(180deg, rgba(255,255,255,.96), rgba(248,250,252,.96));
  backdrop-filter: blur(2px);
  overflow: auto;
}

@media (min-width: 1181px) {
  .layout {
    display: grid;
    /* cat-col | sidebar | main */
    grid-template-columns: 124px minmax(280px, 340px) minmax(0, 1fr);
    grid-template-rows: auto;
    align-items: start;
  }

  .layout--embedded-tile {
    display: block;
  }

  /* Kategorie-Spalte: Root + Unterkategorien gestapelt */
  .cat-col {
    grid-column: 1;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    height: 100vh;
    height: 100dvh;
    border-right: 1px solid rgba(15, 23, 42, 0.08);
    overflow: hidden;
    align-self: start;
  }
  /* Root-Panel füllt verfügbaren Platz, Subcat-Panel passt sich an */
  .cat-col :deep(.cat-panel:first-child) {
    flex: 1 1 auto;
    border-right: 0;
  }
  .subcat-divider {
    flex-shrink: 0;
    height: 1px;
    background: rgba(15, 23, 42, 0.08);
    margin: 0;
  }
  .subcat-panel {
    flex: 0 1 auto;
    max-height: 45%;
    overflow-y: auto;
  }
  .subcat-panel :deep(.cat-panel) {
    border-right: 0;
    height: auto;
  }

  /* Sidebar: links neben Kategorien */
  .sidebar {
    grid-column: 2;
    grid-row: 1;
    position: sticky;
    top: 0;
    height: 100vh;
    height: 100dvh;
    box-sizing: border-box;
    overflow: hidden;
    padding: 0;
    background: #fff;
    border-right: 1px solid rgba(15, 23, 42, 0.08);
    transform: none !important;
    display: flex;
    flex-direction: column;
    align-self: start;
  }

  /* Main (Galerie): rechts */
  .main {
    grid-column: 3;
    grid-row: 1;
    min-width: 0;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: visible;
  }

  .backdrop { display: none; }
}

/* ── Tablet: Kategorien | Main, Sidebar als Overlay ─── */
@media (min-width: 768px) and (max-width: 1180px) {
  .layout {
    position: relative;
    display: grid;
    grid-template-columns: 108px minmax(0, 1fr);
    grid-template-rows: auto;
    align-items: start;
  }
  .cat-col {
    grid-column: 1;
    grid-row: 1;
    display: flex;
    flex-direction: column;
    position: sticky;
    top: env(safe-area-inset-top, 0px);
    height: 100vh;
    height: 100dvh;
    border-right: 1px solid rgba(15, 23, 42, 0.08);
    overflow: hidden;
    align-self: start;
  }
  .cat-col :deep(.cat-panel:first-child) {
    flex: 1 1 auto;
    border-right: 0;
  }
  .main {
    grid-column: 2;
    grid-row: 1;
    min-width: 0;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: visible;
    position: relative;
  }
  .sidebar {
    position: fixed;
    right: 0;
    top: 0;
    height: 100vh;
    height: 100dvh;
    width: min(420px, calc(100vw - 108px));
    box-sizing: border-box;
    background: #fff;
    border-left: 1px solid rgba(15, 23, 42, 0.1);
    transform: translateX(124%);
    transition: transform 0.18s ease;
    z-index: 60;
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .sidebar.open {
    transform: translateX(0);
  }
  .tablet-sidebar-toggle{
    position: fixed;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    z-index: 70;
    min-height: 46px;
    max-width: min(300px, calc(100vw - 148px));
    padding: 0 16px;
    border: 0;
    background: #e30b17;
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 16px 36px rgba(227, 11, 23, 0.28);
  }
  .tablet-sidebar-toggle:disabled{
    opacity: .65;
    cursor: not-allowed;
    box-shadow: none;
  }
}

/* ── Mobile: Cat oben | Main | Sidebar als Overlay ───── */
@media (max-width: 767px) {
  .layout {
    position: relative;
    display: flex;
    flex-direction: column;
    height: auto;
    max-height: none;
    min-height: 100vh;
    min-height: 100dvh;
    overflow: visible;
  }
  .layout--embedded-tile {
    min-height: 0;
  }
  .cat-col {
    order: -1;
    width: 100%;
    flex-shrink: 0;
    position: sticky;
    top: 0;
    z-index: 24;
    background: #fff;
  }
  .main {
    flex: 1;
    min-width: 0;
    padding: 0;
    padding-bottom: 92px;
    overflow: visible;
  }
  .sidebar {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    height: 100vh;
    height: 100dvh;
    box-sizing: border-box;
    width: 100vw;
    max-width: 100vw;
    background: #fff;
    border-left: 0;
    transform: translateX(110%);
    transition: transform 0.18s ease;
    z-index: 60;
    overflow: hidden;
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .sidebar.open { transform: translateX(0); }
  .tablet-sidebar-toggle{
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    z-index: 70;
    width: auto;
    min-height: 48px;
    padding: 0 16px;
    border: 0;
    background: #e30b17;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    box-shadow: 0 16px 36px rgba(227, 11, 23, 0.28);
  }
  .tablet-sidebar-toggle:disabled{
    opacity: .65;
    cursor: not-allowed;
    box-shadow: none;
  }
  .backdrop {
    position: fixed; inset: 0;
    background: rgba(15, 23, 42, 0.35);
    z-index: 50;
  }
}

@media (min-width: 1181px){
  .tablet-sidebar-toggle{
    display: none;
  }
}

/* Shared */
.loading,
.prompt {
  padding: 24px 18px;
  color: rgba(15, 23, 42, 0.55);
  font-size: 13px;
}

.sidebar-close-btn {
  position: absolute;
  top: calc(12px + env(safe-area-inset-top, 0px));
  right: 12px;
  z-index: 120;
  width: 38px;
  height: 38px;
  border: 1px solid rgba(15, 23, 42, 0.18);
  border-radius: 999px;
  background: rgba(255,255,255,.98);
  color: #0f172a;
  cursor: pointer;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 18px rgba(15,23,42,.10);
  backdrop-filter: blur(4px);
}

.sidebar :deep(.sidebar-wrapper) {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
}

@media (max-width: 767px) {
  .sidebar :deep(.sidebar-wrapper) {
    height: 100%;
  }
}

.sidebar.overlay :deep(.sidebar-wrapper) {
  padding-top: calc(56px + env(safe-area-inset-top, 0px));
}

.sidebar :deep(.view),
.sidebar :deep(.overview-view),
.sidebar :deep(.detail-view),
.sidebar :deep(.context-bar),
.sidebar :deep(.ctx-left),
.sidebar :deep(.ctx-titles),
.sidebar :deep(.ctx-title),
.sidebar :deep(.ctx-dropdown),
.sidebar :deep(.multi-hint),
.sidebar :deep(.price-footer),
.sidebar :deep(.price-controls),
.sidebar :deep(.price-actions),
.sidebar :deep(.back-btn) {
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}


.side-overlay {
  border: 1px dashed rgba(15, 23, 42, 0.18);
  padding: 12px;
  margin-bottom: 12px;
  background: rgba(15, 23, 42, 0.02);
}
.hint-title {
  font-weight: 700;
  margin-bottom: 4px;
}
.hint-sub {
  font-size: 13px;
  color: rgba(15, 23, 42, 0.65);
  line-height: 1.35;
}

/* Deaktivieren: Sidebar bleibt sichtbar (Desktop) / öffnet (Mobile) aber ist nicht bedienbar */
.sidebar.disabled :deep(*) {
  pointer-events: none;
}
.sidebar.disabled :deep(.sidebar-logo),
.sidebar.disabled :deep(.sidebar-logo__link),
.sidebar.disabled :deep(.sidebar-logo__img) {
  pointer-events: auto;
}
.sidebar.disabled .side-overlay {
  pointer-events: auto;
}
</style>
